import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase/client';
import { signOut } from '../lib/supabase/auth';
import { getBillingStatus, getManageAccountUrl, getCheckoutUrl } from '../lib/api/billingApi';
import { getUserDetails, updateUserDetails, getGamesList } from '../lib/api/userApi';

const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for tracking sign out process
  const [isSigningOut, setIsSigningOut] = useState(false);
  
  // Parse tab from URL query parameter (e.g., ?tab=subscription)
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    // Only accept valid tab names
    const validTabs = ['profile', 'subscription', 'notifications', 'safety', 'privacy', 'signout'];
    return validTabs.includes(tabParam) ? tabParam : 'profile';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    sessionReminders: true,
    productUpdates: false,
    marketingEmails: false
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDataOnly, setDeleteDataOnly] = useState(false);
  
  // Profile related state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    gender: 0,
    age: 0,
    region: '',
    gameId: 0,
    experience: 0,
    otherGame: '',
    email: user?.email || ''
  });
  const [games, setGames] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(null);
  
  // Billing related state
  const [billingStatus, setBillingStatus] = useState({
    billingAcctSetup: false,
    currentValidSubscription: false,
    cachedCheck: false
  });
  const [isLoadingBillingStatus, setIsLoadingBillingStatus] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Function to fetch profile data - defining this early before it's used in any dependency array
  const fetchProfileData = useCallback(async () => {
    setLoadingProfile(true);
    try {
      // Fetch user details
      const { data: userDetailsData, error: userDetailsError } = await getUserDetails();
      if (userDetailsError) throw userDetailsError;
      
      // Fetch games list
      const { data: gamesData, error: gamesError } = await getGamesList();
      if (gamesError) throw gamesError;
      
      // Debug log the games data
      console.log('Games from API:', gamesData);
      
      // Set data
      setGames(gamesData);
      
      // Set form data from user details
      setProfileData({
        firstName: userDetailsData.firstName || '',
        lastName: userDetailsData.lastName || '',
        preferredName: userDetailsData.preferredName || '',
        gender: userDetailsData.gender || 0,
        age: userDetailsData.age || 0,
        region: userDetailsData.region || '',
        gameId: userDetailsData.gameId || 0,
        experience: userDetailsData.experience || 0,
        otherGame: userDetailsData.otherGame || '',
        email: user?.email || ''
      });
      
      // Debug log the profile data
      console.log('Profile from API:', userDetailsData);
    } catch (err) {
      console.error('Error fetching profile data:', err);
      setProfileError('Failed to load your profile information. Please try again later.');
    } finally {
      setLoadingProfile(false);
    }
  }, [user?.email]);

  // Function to fetch billing status - also defined early before any useEffect
  const fetchBillingStatus = useCallback(async () => {
    setIsLoadingBillingStatus(true);
    try {
      const status = await getBillingStatus();
      setBillingStatus(status);
    } catch (error) {
      console.error('Error fetching billing status:', error);
    } finally {
      setIsLoadingBillingStatus(false);
    }
  }, []);

  // Fetch user profile data when profile tab is opened
  useEffect(() => {
    if (activeTab === 'profile') {
      fetchProfileData();
    }
  }, [activeTab, fetchProfileData]);

  // Fetch billing status when subscription tab is opened
  useEffect(() => {
    if (activeTab === 'subscription') {
      fetchBillingStatus();
    }
  }, [activeTab, fetchBillingStatus]);

  // Handle profile input change
  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: name === 'age' ? parseInt(value, 10) || 0 : value
    });
  };

  // Handle profile select change
  const handleProfileSelectChange = (name, value) => {
    setProfileData({
      ...profileData,
      [name]: value
    });
  };

  // Handle profile form submission
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    setProfileError(null);
    setProfileSuccess(null);

    try {
      const { error: updateError } = await updateUserDetails(profileData);
      
      if (updateError) throw updateError;
      
      setProfileSuccess('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setProfileSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError('Failed to update your profile. Please try again.');
    } finally {
      setUpdatingProfile(false);
    }
  };

  // Gender options
  const genderOptions = [
    { id: 0, text: 'Male' },
    { id: 1, text: 'Female' },
    { id: 2, text: 'Other' },
    { id: 3, text: 'Prefer not to say' }
  ];

  // Experience level options
  const experienceOptions = [
    { id: 0, text: 'Beginner' },
    { id: 1, text: 'Intermediate' },
    { id: 2, text: 'Advanced' },
    { id: 3, text: 'Professional' }
  ];

  // Function to handle managing account (redirect to Stripe portal)
  const handleManageAccount = async () => {
    if (isRedirecting) return;
    
    setIsRedirecting(true);
    try {
      const { redirectUrl } = await getManageAccountUrl();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        console.error('No redirect URL returned');
      }
    } catch (error) {
      console.error('Error getting manage account URL:', error);
    } finally {
      setIsRedirecting(false);
    }
  };

  // Function to handle subscribing (redirect to Stripe checkout)
  const handleSubscribe = async () => {
    if (isRedirecting) return;
    
    setIsRedirecting(true);
    try {
      const { redirectUrl } = await getCheckoutUrl();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        console.error('No redirect URL returned');
      }
    } catch (error) {
      console.error('Error getting checkout URL:', error);
    } finally {
      setIsRedirecting(false);
    }
  };

  // Handler for notification toggle changes
  const handleNotificationChange = (setting) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  // Handle sign out
  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      // The signOut function handles the redirect
    } catch (error) {
      console.error('Error signing out:', error);
      setIsSigningOut(false);
    }
  };

  // Handler for account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== user?.email) {
      setDeleteError('Please enter your email correctly to confirm deletion');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      if (deleteDataOnly) {
        // API call to delete only user data but keep account
        // Replace with your actual API endpoint
        const response = await fetch('/api/v1/user/delete-data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete user data');
        }

        setDeleteSuccess('Your data has been successfully deleted');
      } else {
        // Delete the user account completely via Supabase
        const { error } = await supabase.auth.admin.deleteUser(user.id);
        
        if (error) throw error;
        
        // Sign out the user after successful deletion
        await supabase.auth.signOut();
        setDeleteSuccess('Your account has been successfully deleted');
      }
    } catch (error) {
      console.error('Error deleting account or data:', error);
      setDeleteError(error.message || 'An error occurred during deletion');
    } finally {
      setIsDeleting(false);
    }
  };

  // Update the URL when tab changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', activeTab);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  }, [activeTab, navigate, location.pathname, location.search]);

  // Render appropriate content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        if (loadingProfile) {
          return (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          );
        }
        
        return (
          <div className="settings-profile">
            <h2 className="text-xl font-semibold mb-6">Profile Information</h2>
            
            {profileError && (
              <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
                {profileError}
              </div>
            )}
            
            {profileSuccess && (
              <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg">
                {profileSuccess}
              </div>
            )}
            
            <form onSubmit={handleProfileSubmit} className="settings-profile__form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* First Name */}
                <div className="settings-profile__field">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleProfileInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your first name"
                  />
                </div>
                
                {/* Last Name */}
                <div className="settings-profile__field">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your last name"
                  />
                </div>
                
                {/* Preferred Name */}
                <div className="settings-profile__field">
                  <label htmlFor="preferredName" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Name
                  </label>
                  <input
                    type="text"
                    id="preferredName"
                    name="preferredName"
                    value={profileData.preferredName}
                    onChange={handleProfileInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="What should we call you?"
                  />
                </div>
                
                {/* Age */}
                <div className="settings-profile__field">
                  <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">
                    Age
                  </label>
                  <input
                    type="number"
                    id="age"
                    name="age"
                    value={profileData.age}
                    onChange={handleProfileInputChange}
                    min="8"
                    max="120"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your age"
                  />
                </div>
                
                {/* Region */}
                <div className="settings-profile__field">
                  <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-2">
                    Region
                  </label>
                  <input
                    type="text"
                    id="region"
                    name="region"
                    value={profileData.region}
                    onChange={handleProfileInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your region or country"
                  />
                </div>
                
                {/* Email Address (read-only) */}
                <div className="settings-profile__field">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={profileData.email}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">Email address cannot be changed</p>
                </div>
              </div>
              
              {/* Gender Selection */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-700 mb-4">Gender</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {genderOptions.map((option) => (
                    <div 
                      key={option.id}
                      onClick={() => handleProfileSelectChange('gender', option.id)}
                      className={`p-4 rounded-lg cursor-pointer transition-all ${
                        profileData.gender === option.id
                          ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                          : 'border hover:border-blue-400 hover:shadow-sm'
                      }`}
                    >
                      <h4 className="text-sm font-medium text-gray-800 text-center">{option.text}</h4>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Game/Sport Selection */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-700 mb-4">Primary Game/Sport</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {games
                    .filter(game => game.gameName !== "SYSTEM_OTHER")
                    .sort((a, b) => {
                      if (a.gameName === "General") return 1;
                      if (b.gameName === "General") return -1;
                      return a.gameName.localeCompare(b.gameName);
                    })
                    .map((game) => (
                    <div 
                      key={game.gameId}
                      onClick={() => handleProfileSelectChange('gameId', game.gameId)}
                      className={`p-4 rounded-lg cursor-pointer transition-all ${
                        profileData.gameId === game.gameId
                          ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                          : 'border hover:border-blue-400 hover:shadow-sm'
                      }`}
                    >
                      <h4 className="text-sm font-medium text-gray-800 text-center">
                        {game.gameName}
                      </h4>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Experience Level */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-700 mb-4">Experience Level</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {experienceOptions.map((option) => (
                    <div 
                      key={option.id}
                      onClick={() => handleProfileSelectChange('experience', option.id)}
                      className={`p-4 rounded-lg cursor-pointer transition-all ${
                        profileData.experience === option.id
                          ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                          : 'border hover:border-blue-400 hover:shadow-sm'
                      }`}
                    >
                      <h4 className="text-sm font-medium text-gray-800 text-center">{option.text}</h4>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="settings-profile__actions">
                <button 
                  type="submit"
                  className="settings-profile__save-btn bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg transition-colors font-medium disabled:bg-blue-300 disabled:cursor-not-allowed"
                  disabled={updatingProfile}
                >
                  {updatingProfile ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
          </div>
        );
        
      case 'subscription':
        return (
          <div className="settings-subscription">
            <h2 className="text-xl font-semibold mb-6">Subscription Management</h2>
            
            {isLoadingBillingStatus ? (
              <div className="bg-gray-50 rounded-lg p-6 mb-6 flex justify-center">
                <div className="settings-subscription__loading">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2">Loading subscription status...</span>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="settings-subscription__status mb-4">
                  <h3 className="text-lg font-medium mb-2">Subscription Status</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    <li className="text-gray-700">
                      Billing Account: {billingStatus.billingAcctSetup ? (
                        <span className="text-green-600 font-medium">Set up</span>
                      ) : (
                        <span className="text-gray-600">Not set up</span>
                      )}
                    </li>
                    <li className="text-gray-700">
                      Active Subscription: {billingStatus.currentValidSubscription ? (
                        <span className="text-green-600 font-medium">Active</span>
                      ) : (
                        <span className="text-red-600">Inactive</span>
                      )}
                    </li>
                  </ul>
                </div>
                
                <p className="text-gray-700 mb-4">
                  {billingStatus.currentValidSubscription 
                    ? 'Manage your subscription plan, billing information, and payment methods.'
                    : 'Subscribe to get access to premium features and support.'}
                </p>
                
                <div className="settings-subscription__actions flex flex-wrap gap-3">
                  {billingStatus.billingAcctSetup && (
                    <button 
                      className="settings-subscription__manage-btn bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-colors duration-300 disabled:bg-blue-300 disabled:cursor-not-allowed"
                      onClick={handleManageAccount}
                      disabled={isRedirecting}
                    >
                      {isRedirecting ? 'Redirecting...' : 'Manage Subscription'}
                    </button>
                  )}
                  
                  {!billingStatus.currentValidSubscription && (
                    <button 
                      className="settings-subscription__subscribe-btn bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-md transition-colors duration-300 disabled:bg-green-300 disabled:cursor-not-allowed"
                      onClick={handleSubscribe}
                      disabled={isRedirecting}
                    >
                      {isRedirecting ? 'Redirecting...' : 'Subscribe'}
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {billingStatus.billingAcctSetup && (
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-2">Billing History</h3>
                <p className="text-gray-600 mb-4">View and download your past invoices.</p>
                
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Apr 15, 2023</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">$19.99</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Paid</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-500 hover:text-blue-700 cursor-pointer">Download</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Mar 15, 2023</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">$19.99</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Paid</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-500 hover:text-blue-700 cursor-pointer">Download</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
        
      case 'notifications':
        return (
          <div className="settings-notifications">
            <h2 className="text-xl font-semibold mb-6">Notification Preferences</h2>
            
            <div className="space-y-6">
              <div className="settings-notifications__option">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Email Notifications</h3>
                    <p className="text-gray-600">Receive emails about your account and activity</p>
                  </div>
                  <label className="settings-notifications__toggle relative inline-block w-14 h-7">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.emailNotifications}
                      onChange={() => handleNotificationChange('emailNotifications')}
                      className="opacity-0 w-0 h-0"
                    />
                    <span className={`absolute cursor-pointer inset-0 rounded-full transition-colors duration-300 ${notificationSettings.emailNotifications ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <span className={`absolute w-5 h-5 bg-white rounded-full transition-transform duration-300 transform ${notificationSettings.emailNotifications ? 'translate-x-7' : 'translate-x-1'} top-1`}></span>
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="settings-notifications__option">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Session Reminders</h3>
                    <p className="text-gray-600">Get reminders about scheduled sessions</p>
                  </div>
                  <label className="settings-notifications__toggle relative inline-block w-14 h-7">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.sessionReminders}
                      onChange={() => handleNotificationChange('sessionReminders')}
                      className="opacity-0 w-0 h-0"
                    />
                    <span className={`absolute cursor-pointer inset-0 rounded-full transition-colors duration-300 ${notificationSettings.sessionReminders ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <span className={`absolute w-5 h-5 bg-white rounded-full transition-transform duration-300 transform ${notificationSettings.sessionReminders ? 'translate-x-7' : 'translate-x-1'} top-1`}></span>
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="settings-notifications__option">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Product Updates</h3>
                    <p className="text-gray-600">Stay informed about new features and improvements</p>
                  </div>
                  <label className="settings-notifications__toggle relative inline-block w-14 h-7">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.productUpdates}
                      onChange={() => handleNotificationChange('productUpdates')}
                      className="opacity-0 w-0 h-0"
                    />
                    <span className={`absolute cursor-pointer inset-0 rounded-full transition-colors duration-300 ${notificationSettings.productUpdates ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <span className={`absolute w-5 h-5 bg-white rounded-full transition-transform duration-300 transform ${notificationSettings.productUpdates ? 'translate-x-7' : 'translate-x-1'} top-1`}></span>
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="settings-notifications__option">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Marketing Emails</h3>
                    <p className="text-gray-600">Receive promotional offers and newsletters</p>
                  </div>
                  <label className="settings-notifications__toggle relative inline-block w-14 h-7">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.marketingEmails}
                      onChange={() => handleNotificationChange('marketingEmails')}
                      className="opacity-0 w-0 h-0"
                    />
                    <span className={`absolute cursor-pointer inset-0 rounded-full transition-colors duration-300 ${notificationSettings.marketingEmails ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <span className={`absolute w-5 h-5 bg-white rounded-full transition-transform duration-300 transform ${notificationSettings.marketingEmails ? 'translate-x-7' : 'translate-x-1'} top-1`}></span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <button 
                className="settings-notifications__save-btn bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-colors duration-300"
                onClick={() => alert('Settings saved successfully!')}
              >
                Save Preferences
              </button>
            </div>
          </div>
        );
        
      case 'safety':
        return (
          <div className="settings-safety">
            <h2 className="text-xl font-semibold mb-6">Account Safety</h2>
            
            <div className="settings-safety__section mb-8">
              <h3 className="text-lg font-medium mb-2">Password Management</h3>
              <p className="text-gray-600 mb-4">
                It's a good idea to use a strong password that you're not using elsewhere
              </p>
              
              <button 
                className="settings-safety__password-btn bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-colors duration-300"
                onClick={() => alert('Password reset email sent!')}
              >
                Change Password
              </button>
            </div>
            
            <div className="settings-safety__section mb-8 pt-8 border-t">
              <h3 className="text-lg font-medium mb-2">Two-Factor Authentication</h3>
              <p className="text-gray-600 mb-4">
                Add an extra layer of security to your account by requiring a verification code
              </p>
              
              <button 
                className="settings-safety__2fa-btn bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition-colors duration-300"
                onClick={() => alert('Two-factor authentication setup coming soon')}
              >
                Enable Two-Factor Authentication
              </button>
            </div>
            
            <div className="settings-safety__section mb-8 pt-8 border-t">
              <h3 className="text-lg font-medium mb-2">Logged-in Devices</h3>
              <p className="text-gray-600 mb-4">
                Review and manage devices that are currently signed in to your account
              </p>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">Current Device</p>
                    <p className="text-sm text-gray-500">Windows PC - Chrome Browser</p>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                </div>
              </div>
              
              <button 
                className="settings-safety__devices-btn bg-red-100 hover:bg-red-200 text-red-700 py-2 px-4 rounded-md transition-colors duration-300"
                onClick={() => alert('This would sign out all other devices')}
              >
                Sign Out All Other Devices
              </button>
            </div>
          </div>
        );
        
      case 'privacy':
        return (
          <div className="settings-privacy">
            <h2 className="text-xl font-semibold mb-6">Privacy & Data</h2>
            
            <div className="settings-privacy__section mb-8">
              <h3 className="text-lg font-medium mb-2">Data Export</h3>
              <p className="text-gray-600 mb-4">Download all your data in a portable format.</p>
              
              <button 
                className="settings-privacy__export-btn bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition-colors duration-300"
                onClick={() => alert('Data export requested. You will receive an email with your data soon.')}
              >
                Request Data Export
              </button>
            </div>
            
            <div className="settings-privacy__section mb-8 border-t pt-8">
              <h3 className="text-lg font-medium mb-2">Delete Your Data</h3>
              <p className="text-gray-600 mb-4">
                This will remove all your personal data, but keep your account active.
              </p>
              
              <div className="mb-4">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={deleteDataOnly} 
                    onChange={() => setDeleteDataOnly(!deleteDataOnly)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-gray-700">
                    I want to delete my data but keep my account
                  </span>
                </label>
              </div>
              
              <button 
                className="settings-privacy__delete-data-btn bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded-md transition-colors duration-300"
                onClick={() => {
                  if (deleteDataOnly) {
                    if (window.confirm('Are you sure you want to delete all your data? This cannot be undone.')) {
                      setActiveTab('delete');
                    }
                  } else {
                    alert('Please check the box to confirm you want to delete only your data.');
                  }
                }}
              >
                Delete My Data
              </button>
            </div>
            
            <div className="settings-privacy__section border-t pt-8">
              <h3 className="text-lg font-medium mb-2 text-red-600">Delete Account</h3>
              <p className="text-gray-600 mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              
              <button 
                className="settings-privacy__delete-account-btn bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md transition-colors duration-300"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete your account? This cannot be undone.')) {
                    setDeleteDataOnly(false);
                    setActiveTab('delete');
                  }
                }}
              >
                Delete My Account
              </button>
            </div>
          </div>
        );
        
      case 'delete':
        return (
          <div className="settings-delete">
            <h2 className="text-xl font-semibold mb-6 text-red-600">
              {deleteDataOnly ? 'Delete Your Data' : 'Delete Your Account'}
            </h2>
            
            {deleteSuccess ? (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                <div className="flex">
                  <div>
                    <p className="text-green-700">
                      {deleteSuccess}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                  <div className="flex">
                    <div>
                      <p className="text-red-700">
                        {deleteDataOnly 
                          ? 'This will permanently delete all your data, but your account will remain active. This action cannot be undone.' 
                          : 'This will permanently delete your account and all associated data. This action cannot be undone.'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {deleteError && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                    <div className="flex">
                      <div>
                        <p className="text-red-700">{deleteError}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mb-6">
                  <label className="block text-gray-700 mb-2">
                    {`Type your email (${user?.email}) to confirm`}
                  </label>
                  <input 
                    type="email" 
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your email address"
                  />
                </div>
                
                <div className="flex space-x-4">
                  <button 
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md transition-colors duration-300"
                    onClick={() => setActiveTab('privacy')}
                  >
                    Cancel
                  </button>
                  
                  <button 
                    className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md transition-colors duration-300 disabled:bg-red-300 disabled:cursor-not-allowed"
                    onClick={handleDeleteAccount}
                    disabled={isDeleting || deleteConfirmation !== user?.email}
                  >
                    {isDeleting ? 'Processing...' : deleteDataOnly ? 'Delete My Data' : 'Delete My Account'}
                  </button>
                </div>
              </>
            )}
          </div>
        );
      
      case 'signout':
        return (
          <div className="settings-signout">
            <h2 className="text-xl font-semibold mb-6">Sign Out</h2>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                Ready to leave? You can sign back in anytime.
              </p>
              
              <button 
                className="settings-signout__button bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md transition-colors duration-300 disabled:bg-red-300 disabled:cursor-not-allowed"
                onClick={handleSignOut}
                disabled={isSigningOut}
              >
                {isSigningOut ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Signing Out...
                  </>
                ) : 'Sign Out'}
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="settings-page max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-8 border-b pb-4">Settings</h1>
      
      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar navigation */}
        <div className="settings-page__sidebar w-full md:w-64 flex-shrink-0">
          <nav className="settings-page__nav space-y-1">
            <button
              className={`settings-page__nav-item w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                activeTab === 'profile' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            <button
              className={`settings-page__nav-item w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                activeTab === 'subscription' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('subscription')}
            >
              Subscription
            </button>
            <button
              className={`settings-page__nav-item w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                activeTab === 'notifications' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
            </button>
            <button
              className={`settings-page__nav-item w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                activeTab === 'safety' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('safety')}
            >
              Safety
            </button>
            <button
              className={`settings-page__nav-item w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                activeTab === 'privacy' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('privacy')}
            >
              Privacy
            </button>
            <button
              className={`settings-page__nav-item w-full text-left px-4 py-2 rounded-md transition-colors duration-200 ${
                activeTab === 'signout' ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setActiveTab('signout')}
            >
              Sign Out
            </button>
          </nav>
        </div>
        
        {/* Main content area */}
        <div className="settings-page__content flex-grow bg-white shadow-sm rounded-lg p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 